"use client";

import { m, useReducedMotion } from "motion/react";
import Image from "next/image";

import { buildTrackerProps, logGAEvent, mergeClasses } from "@trex-2b/libs";
import { GROWTH_ROUTES } from "@trex-2b/libs/config/routes";
import { useLandingLoginGuard } from "@trex-2b/ui/auth/use-landing-login-guard";

// Figma 导出里含 inset #999，浏览器里会落成清晰可见的灰圈；这里保留浅色内高光、去掉灰描边与过重灰雾
const glassInsetHighlight =
  "shadow-[inset_0_1px_0_0_rgba(255,255,255,0.55),inset_0_-0.5px_0_0_rgba(255,255,255,0.25)]";

export function LandingHero() {
  const { runWithLogin } = useLandingLoginGuard();
  const shouldReduceMotion = useReducedMotion();

  return (
    <section
      aria-labelledby="landing-hero-headline"
      className="relative flex w-full flex-col items-center bg-[#232323] px-[144px] pt-[58px] pb-[46px]"
    >
      {/*
        1512 artboard: caps `8788`-style at x≈156 / right inset 133 (= 1512 − 1206 − 173).
        Wrap in max-w-[1512px] so coordinates stay aligned with the centered title on wide viewports.
      */}
      <div
        className="pointer-events-none absolute inset-0 z-0 overflow-x-hidden overflow-y-visible max-md:hidden"
        aria-hidden
      >
        <div className="relative mx-auto h-full w-full max-w-[1512px]">
          <div className="absolute top-[calc(50%-36px)] right-[133px] h-[14px] w-[173px] -translate-y-1/2">
            <div className="pointer-events-none absolute inset-0 overflow-hidden">
              <Image
                alt=""
                src="/images/landing/hero-normal-reverse.png"
                width={173}
                height={14}
                className="absolute top-[-3.11%] left-0 h-[106.22%] max-w-none w-full"
              />
            </div>
          </div>
          <div className="absolute top-[calc(50%-36px)] left-[156px] h-[14px] w-[173px] -translate-y-1/2">
            <div className="pointer-events-none absolute inset-0 overflow-hidden">
              <Image
                alt=""
                src="/images/landing/hero-normal.png"
                width={173}
                height={14}
                className="absolute top-[-3.11%] left-0 h-[106.22%] max-w-none w-full"
              />
            </div>
          </div>
        </div>
      </div>

      <m.div
        className="relative z-1 flex w-full flex-col items-center gap-[28px]"
        initial={shouldReduceMotion ? "visible" : "hidden"}
        whileInView="visible"
        viewport={{ amount: 0.45, once: true }}
        variants={{
          hidden: {},
          visible: {
            transition: { delayChildren: 0.08, staggerChildren: 0.12 },
          },
        }}
      >
        <m.h1
          className="relative flex w-[831px] max-w-full shrink-0 flex-col justify-center font-medium text-[56px] text-white capitalize not-italic leading-0 tracking-[-3.6px] text-center [font-family:var(--font-ancizar)] max-md:text-[40px] max-md:tracking-[-2px]"
          id="landing-hero-headline"
          variants={{
            hidden: { opacity: 0, y: 26, filter: "blur(8px)" },
            visible: {
              opacity: 1,
              y: 0,
              filter: "blur(0px)",
              transition: { duration: 0.62, ease: [0.16, 1, 0.3, 1] },
            },
          }}
        >
          <p className="text-[56px] leading-[66px] max-md:text-[40px] max-md:leading-[48px]">
            <span className="block text-center lg:whitespace-nowrap">
              You are the&nbsp;product.
            </span>
            <span className="block text-center lg:whitespace-nowrap">
              They are the&nbsp;market.
            </span>
            <span className="block text-center lg:whitespace-nowrap">
              We handle the&nbsp;fit.
            </span>
          </p>
        </m.h1>
        <m.button
          type="button"
          data-testid="landing-hero-get-started-btn"
          onClick={() => {
            logGAEvent("cta_click", {
              cta_id: "landing_hero_get_start",
              cta_text: "Get start",
              target_route: GROWTH_ROUTES.ON_BOARDING,
            });
            runWithLogin(() => {
              window.open(GROWTH_ROUTES.ON_BOARDING);
            });
          }}
          className={mergeClasses(
            "relative inline-flex h-[46px] w-[177px] shrink-0 cursor-pointer items-center gap-[10.783px] overflow-hidden rounded-[134px] border-0 px-[28.304px] py-[12px]",
            "shadow-[0px_0px_2.696px_0px_rgba(0,0,0,0.1),0px_1.348px_10.783px_0px_rgba(0,0,0,0.1)]",
            "bg-transparent",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-400/60 focus-visible:ring-offset-2 focus-visible:ring-offset-[#232323]",
          )}
          whileTap={{ scale: 0.8 }}
          variants={{
            hidden: { opacity: 0, y: 16, scale: 0.95 },
            visible: {
              opacity: 1,
              y: 0,
              scale: 1,
              transition: { duration: 0.52, ease: [0.16, 1, 0.3, 1] },
            },
          }}
          transition={{ type: "spring", stiffness: 520, damping: 18 }}
          {...buildTrackerProps()}
        >
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 rounded-[134px] bg-[rgba(255,255,255,0.82)] backdrop-blur-[8.087px]"
          />
          <div className="font-inter relative flex shrink-0 flex-col justify-center text-[16px] leading-0 font-semibold tracking-[-0.3px] whitespace-nowrap text-[#1c1916] not-italic">
            <p className="leading-[30.8px]">Get Started</p>
          </div>
          <div className="relative size-[42px] shrink-0">
            <Image
              alt=""
              width={42}
              height={42}
              src="/images/landing/hero-cta-arrow-right-contained-02.svg"
              className="absolute inset-0 block size-full max-w-none"
            />
          </div>
          <div
            aria-hidden
            className={mergeClasses(
              "pointer-events-none absolute inset-0 rounded-[inherit]",
              glassInsetHighlight,
            )}
          />
        </m.button>
      </m.div>
    </section>
  );
}
